import tzlookup from 'tz-lookup';

/**
 * Resolve the IANA timezone for a physical location from its coordinates.
 *
 * Uses tz-lookup, an offline lat/lon → timezone-boundary lookup, so a
 * listing's stored timezone reflects where the listing *is* rather than the
 * publisher's browser. Returns null when coordinates are missing or out of
 * range (tz-lookup throws on invalid input) so callers can fall back — e.g.
 * to the browser's timezone for a listing that has no location yet.
 */
export function deriveTimezone(
  lat?: number | null,
  lon?: number | null,
): string | null {
  if (lat == null || lon == null) return null;
  try {
    return tzlookup(lat, lon);
  } catch {
    return null;
  }
}

/**
 * Short, human-friendly timezone abbreviation (e.g. "MDT", "EST") for a zone
 * at a specific instant. The instant matters because the abbreviation is
 * DST-dependent — the same zone reads "MDT" in summer and "MST" in winter, so
 * callers should pass the moment the label describes (e.g. a listing's window
 * start). Some zones have no letter abbreviation; Intl then yields a GMT offset
 * (e.g. "GMT-6"), which we return as-is. Returns null on an invalid zone.
 */
export function timeZoneAbbreviation(
  timezone: string,
  at: Date,
): string | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts(at);
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? null;
  } catch {
    return null;
  }
}

/**
 * The UTC offset (in minutes) of a zone at a specific instant. Computed by
 * formatting the instant in the target zone and diffing the wall-clock reading
 * against the true UTC time — the standard Intl technique, correct across DST
 * and half-hour zones. Returns null on an invalid zone.
 *
 * Comparing two zones' offsets at the same instant is the meaningful test for
 * "are these actually different times" — two zones can share an offset (e.g.
 * America/New_York and America/Detroit) and thus show identical wall-clock
 * times, in which case labeling one as "different" would just be noise.
 */
/**
 * Interpret a naive wall-clock timestamp ("YYYY-MM-DDTHH:mm[:ss]", NO offset)
 * as a time in the given IANA zone and return the corresponding absolute
 * instant.
 *
 * Listing time windows are stored as the lister's local wall-clock plus a
 * `timezone` field — "6:30pm in America/Chicago" is stored as "…T18:30:00" +
 * "America/Chicago". Plain `new Date(naive)` instead reads that string in the
 * VIEWER's browser zone, which shifts the displayed time by the offset between
 * the two zones whenever they differ. Resolving it in the listing's own zone
 * keeps "6:30pm" meaning 6:30pm there, for every viewer.
 *
 * Falls back to browser-local parsing when the zone is missing/invalid so
 * legacy windows that never carried a timezone keep their prior behavior.
 */
export function zonedWallClockToInstant(
  naive: string,
  timeZone: string | null | undefined,
): Date {
  if (!timeZone) return new Date(naive);
  const [datePart, timePart = '00:00:00'] = naive.split('T');
  const [y, mo, d] = datePart.split('-').map(Number);
  const [h, mi, s = 0] = timePart.split(':').map(Number);
  if ([y, mo, d, h, mi].some((n) => Number.isNaN(n))) return new Date(naive);
  // Treat the wall-clock as if it were UTC, then correct by the zone's offset
  // at that guess. Offsets move by at most an hour across DST, so a single
  // correction resolves every non-transition time exactly.
  const guessUTC = Date.UTC(y, mo - 1, d, h, mi, s);
  const offset = zoneOffsetMinutes(timeZone, new Date(guessUTC));
  if (offset == null) return new Date(naive);
  return new Date(guessUTC - offset * 60000);
}

/**
 * Format an absolute instant as "MM/DD/YY H:MM am/pm TZ" in the given zone
 * (year optional). Used on order-detail / earnings rows where the reader
 * wants to know when something happened at the LISTING's location, not at
 * the viewer's browser location — a chef in Denver looking at an order that
 * pickup'd in Vail still wants Vail's clock. Falls back to browser tz when
 * the passed zone is missing or invalid so legacy rows without a stored
 * timezone still render something sensible.
 *
 * Options:
 *   - year: include "/YY" on the date (default true). The earnings list
 *     drops it (short retention window, no ambiguity in practice).
 *
 * am/pm are rendered lowercase — Intl `en-US` gives them uppercase; we
 * post-process rather than fight the locale so future locales that
 * naturally lower-case (or omit) the period reader keep working.
 */
export function formatListingTimestamp(
  iso: string | number | Date,
  timezone?: string | null,
  options?: { year?: boolean },
): string {
  const at = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const zone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const includeYear = options?.year !== false;
  const datePart = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    ...(includeYear ? { year: '2-digit' as const } : {}),
    month: '2-digit',
    day: '2-digit',
  }).format(at);
  const timePart = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
    .format(at)
    .replace(/\sAM\b/, ' am')
    .replace(/\sPM\b/, ' pm');
  const tzLabel = timeZoneAbbreviation(zone, at) ?? '';
  return tzLabel ? `${datePart} ${timePart} ${tzLabel}` : `${datePart} ${timePart}`;
}

export function zoneOffsetMinutes(timezone: string, at: Date): number | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(at);
    const p: Record<string, string> = {};
    for (const part of parts) p[part.type] = part.value;
    // Intl renders midnight as hour "24"; normalize so Date.UTC doesn't roll
    // the day forward.
    const hour = p.hour === '24' ? 0 : Number(p.hour);
    const asUTC = Date.UTC(
      Number(p.year),
      Number(p.month) - 1,
      Number(p.day),
      hour,
      Number(p.minute),
      Number(p.second),
    );
    return Math.round((asUTC - at.getTime()) / 60000);
  } catch {
    return null;
  }
}
